import { getPairProducts } from "./tirePairModel";

// Set displaySize lists the rear size first, then " + ", then the front size.
export function parseSetAxleSizes(displaySize) {
  if (typeof displaySize !== "string" || displaySize.indexOf(" + ") === -1) {
    return null;
  }

  const parts = displaySize.split(" + ").map((part) => part.trim());
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }

  return { rear: parts[0], front: parts[1] };
}

// "120/70ZR17 58W", "180/55 R17 73W", "90/90-21 54R".
const EMBEDDED_LOAD_SPEED_RE =
  /(\d{2,3})\s*\/\s*(\d{2,3})\s*(?:[Zz]?[Rr]|-)?\s*(\d{2})(?:\s+(\d{2,3})\s*(\(?[A-Za-z]{1,2}\)?))?/;

function normalizeSpeedCode(speed) {
  const raw = String(speed || "").trim();
  if (!raw) return "";
  if (raw.toLowerCase() === "(w)") return "(W)";
  return raw.toUpperCase();
}

function pickCode(primary, fallback) {
  const value = primary == null ? "" : String(primary).trim();
  if (value && value.toLowerCase() !== "n/a") return value;
  return fallback == null ? "" : String(fallback).trim();
}

function compact(value) {
  return String(value || "").replace(/[\s()]/g, "").toUpperCase();
}

export function parseLoadSpeedFromSize(sizeStr) {
  const match = String(sizeStr || "").match(EMBEDDED_LOAD_SPEED_RE);
  if (!match || !match[4] || !match[5]) {
    return { load: "", speed: "" };
  }

  return {
    load: match[4],
    speed: normalizeSpeedCode(match[5]),
  };
}

// Prefer explicit load/speed fields, then a rating already written on the size.
// Show whichever of the two we have ("58W", "58", or "W").
export function formatTireSizeLabel(size, load, speed, separator = " ") {
  const sizeText = size == null ? "" : String(size).trim();
  const parsed = parseLoadSpeedFromSize(sizeText);
  const loadText = pickCode(load, parsed.load);
  const speedText = normalizeSpeedCode(pickCode(speed, parsed.speed));
  const rating = `${loadText}${speedText}`;

  if (!sizeText) return rating;
  if (!rating) return sizeText;
  if (compact(sizeText).indexOf(compact(rating)) !== -1) return sizeText;
  return `${sizeText}${separator}${rating}`;
}

// One label for a single tire, or "rear + front" when the product is a set.
export function formatProductSizeLabel(product, separator = " ") {
  const item = product || {};
  const raw = item.displaySize || item.size || "";

  if (item.set || item.atvSet) {
    const parsed = parseSetAxleSizes(raw);
    if (parsed) {
      const rear = formatTireSizeLabel(parsed.rear, item.rearLoad, item.rearSpeed, separator);
      const front = formatTireSizeLabel(parsed.front, item.frontLoad, item.frontSpeed, separator);
      return [rear, front].filter(Boolean).join(" + ");
    }
  }

  return formatTireSizeLabel(raw, item.load || item.loadIndex, item.speed || item.speedIndex, separator);
}

export function readProductScore(product) {
  if (!product || product.score == null || product.score === "") {
    return null;
  }

  const value = Number(product.score);
  return Number.isFinite(value) ? value : null;
}

// One card-level score only when every tire agrees. Differing scores stay on each axle.
export function getSharedPairScore(pair) {
  const products = getPairProducts(pair);
  if (products.length === 0) {
    return null;
  }

  const scores = products.map(readProductScore);
  if (scores.some((score) => score == null)) {
    return null;
  }

  const first = scores[0];
  return scores.every((score) => score === first) ? first : null;
}
